import {
  type CharacterShape,
  type StrokeRun,
  strokeOf,
} from "./stroke-shape.js";

/**
 * One character of the handwriting index, as a line of text.
 *
 * The index is published as a text file under `/data/`, one line per character,
 * and this module is both ends of that format. `src/assets/handwriting-data.ts`
 * writes the lines from `hanzi-writer-data` and
 * `src/handwriting/handwriting-index.ts` reads them back in the browser.
 *
 * **Text rather than packed bytes, and it is the smaller of the two.** The same
 * descriptions as three raw bytes per run come to 563 KB, and 415 KB brotlied.
 * Written as three characters of a 64-letter alphabet they come to 568 KB, and
 * **273 KB** brotlied. Brotli finds the repetition in a restricted alphabet on
 * aligned boundaries and finds much less of it in packed bytes. Text also goes
 * through `publishIndexFile` exactly as the 词典 address index does, and can be
 * read with `less`.
 *
 * A line is the character, then one group per stroke in writing order,
 * separated by spaces, each group three letters per straight run.
 *
 * ```text
 * 二PWLPWD
 * 十PWCJC7
 * ```
 *
 * 二 is two strokes of one run each and 十 is a 横 and a 竖.
 */

/**
 * The 64 letters a figure is written as, in value order.
 *
 * URL-safe base64's alphabet. The order is arbitrary and the set is not. Every
 * letter has to survive a text file, a fetch and a `split`, which rules out the
 * space this format separates strokes with and the newline it separates
 * characters with.
 */
const letters =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

/** How many letters one straight run is written as. */
const runLetters = 3;

/** What separates one stroke's runs from the next stroke's. */
const strokeSeparator = " ";

/**
 * The letter a figure is written as.
 *
 * `charAt` rather than an index and a fallback. Every figure in a shape is put
 * on the grid by `stroke-shape.ts`, so there is no value this can be given that
 * the alphabet has no letter for, and a fallback would be a line no input
 * reaches.
 */
function letterFor(value: number): string {
  return letters.charAt(value);
}

/** The figure a letter stands for, or -1 for a letter this format has none of. */
function valueOf(letter: string): number {
  return letters.indexOf(letter);
}

/**
 * Writes a shape as the letters a line of the index carries after the
 * character.
 *
 * Exported because 复习 posts one. A handwriting card sends the ink the reader
 * drew to the handler to be marked, and the ink is a shape with no character
 * to its name: what it is is the question. Writing it in the published format
 * means one description of a shape rather than two, and the string a reader's
 * hand produces is short enough to travel in a form field. See
 * `src/fuxi/grade-handwriting.ts`.
 */
export function shapeText(shape: CharacterShape): string {
  return shape
    .map((stroke) =>
      stroke.runs
        .map(
          (run) =>
            `${letterFor(run.direction)}${letterFor(run.length)}${letterFor(run.place)}`,
        )
        .join(""),
    )
    .join(strokeSeparator);
}

/** Writes one character's shape as the line the index publishes. */
export function shapeLine(character: string, shape: CharacterShape): string {
  return `${character}${shapeText(shape)}`;
}

/** One line of the index, once it has been read. */
export interface ShapeLine {
  /** The character the line describes. */
  readonly character: string;

  /** Its strokes, in writing order. */
  readonly shape: CharacterShape;
}

/**
 * Reads one stroke's letters back into runs.
 *
 * A stroke whose letters do not divide into whole runs is refused outright.
 * Reading the runs that do fit and dropping the tail would take a line the
 * download cut short and hand back a character short of a stroke, which is
 * worse than the missing character: it would be compared against, and it would
 * rank.
 */
function strokeFrom(letters_: string): readonly StrokeRun[] {
  const runs: StrokeRun[] = [];

  if (letters_.length === 0 || letters_.length % runLetters !== 0) {
    return [];
  }

  for (let at = 0; at + runLetters <= letters_.length; at += runLetters) {
    const direction = valueOf(letters_[at] as string);
    const length = valueOf(letters_[at + 1] as string);
    const place = valueOf(letters_[at + 2] as string);

    if (direction < 0 || length < 0 || place < 0) {
      return [];
    }

    runs.push({ direction, length, place });
  }

  return runs;
}

/**
 * Reads a shape back, or nothing where the letters are not one.
 *
 * The other end of {@link shapeText}, and what a posted handwriting answer
 * arrives as. Refused rather than salvaged, for the reason `strokeFrom` gives:
 * a shape read a stroke short would be marked, and it would be marked against
 * the wrong character.
 *
 * An empty string is a shape of no strokes rather than a malformed one. That
 * is a reader who pressed Check with nothing on the square, which is an answer
 * and is marked as one.
 */
export function readShapeText(text: string): CharacterShape | undefined {
  if (text === "") {
    return [];
  }

  const strokes = text
    .split(strokeSeparator)
    .map((stroke) => strokeFrom(stroke));

  return strokes.some((runs) => runs.length === 0)
    ? undefined
    : strokes.map((runs) => strokeOf(runs));
}

/**
 * Reads one line of the index, or nothing where the line is malformed.
 *
 * A line the browser cannot read is dropped and the rest of the index is kept.
 * The file is built by `pnpm run assets` from a package on disk, so a bad line
 * means a truncated download rather than a bad character, and a reader who has
 * lost the tail of the file is better served by the characters that arrived.
 */
export function readShapeLine(line: string): ShapeLine | undefined {
  const code = line.codePointAt(0);

  if (code === undefined) {
    return undefined;
  }

  // By code point rather than by UTF-16 unit. Several 词典 headwords are
  // characters outside the basic multilingual plane, and taking one unit off
  // the front of such a line would leave half a surrogate pair on the front of
  // the first stroke.
  const character = String.fromCodePoint(code);

  if (line.length <= character.length) {
    return undefined;
  }

  const shape = readShapeText(line.slice(character.length));

  return shape === undefined || shape.length === 0
    ? undefined
    : { character, shape };
}
